import { useCallback, useEffect, useRef } from 'react';
import { userActionLogger } from '../lib/userActionLogger';
import { UserActionRequest } from '../types';

export interface UseUserActionLoggerOptions {
  autoTrackPageView?: boolean;
  pageId?: string;
  componentName?: string;
}

export function useUserActionLogger(options: UseUserActionLoggerOptions = {}) {
  const {
    autoTrackPageView = true,
    pageId,
    componentName
  } = options;

  const mountTimeRef = useRef<number>(Date.now());
  const pageViewTrackedRef = useRef<boolean>(false);

  // Auto-track page view on mount
  useEffect(() => {
    if (autoTrackPageView && !pageViewTrackedRef.current) {
      const currentPageId = pageId || window.location.pathname;
      userActionLogger.pageView(currentPageId, {
        component: componentName,
        mount_time: mountTimeRef.current
      });
      pageViewTrackedRef.current = true;
    }
  }, [autoTrackPageView, pageId, componentName]);

  // Track component unmount time
  useEffect(() => {
    return () => {
      if (componentName) {
        userActionLogger.log({
          action_type: 'component_unmount',
          item_type: 'page',
          item_id: componentName,
          metadata: {
            session_duration: Date.now() - mountTimeRef.current
          }
        });
      }
    };
  }, [componentName]);

  // Logging functions with component context
  const logAction = useCallback(async (actionData: Omit<UserActionRequest, 'session_id'>) => {
    const enrichedData = {
      ...actionData,
      metadata: {
        component: componentName,
        ...actionData.metadata
      }
    };
    return userActionLogger.log(enrichedData);
  }, [componentName]);

  const logClick = useCallback(async (
    element: string | HTMLElement,
    metadata?: Record<string, any>
  ) => {
    return userActionLogger.click(element, {
      component: componentName,
      ...metadata
    });
  }, [componentName]);

  const logFormSubmit = useCallback(async (
    formId: string,
    formData?: Record<string, any>,
    success: boolean = true,
    errorMessage?: string
  ) => {
    return userActionLogger.formSubmit(formId, formData, success, errorMessage);
  }, []);

  const logSearch = useCallback(async (
    query: string,
    resultCount?: number,
    metadata?: Record<string, any>
  ) => {
    return userActionLogger.search(query, resultCount, {
      component: componentName,
      ...metadata
    });
  }, [componentName]);

  const logError = useCallback(async (
    errorType: string,
    errorMessage: string,
    context?: Record<string, any>
  ) => {
    return userActionLogger.error(errorType, errorMessage, {
      component: componentName,
      ...context
    });
  }, [componentName]);

  const logDownload = useCallback(async (
    fileId: string,
    fileName?: string,
    metadata?: Record<string, any>
  ) => {
    return userActionLogger.download(fileId, fileName, {
      component: componentName,
      ...metadata
    });
  }, [componentName]);

  // Performance tracking
  const trackResponseTime = useCallback(async (
    action: string,
    startTime: number,
    metadata?: Record<string, any>
  ) => {
    return userActionLogger.trackResponseTime(action, startTime, {
      component: componentName,
      ...metadata
    });
  }, [componentName]);

  // Helper for tracking async operations
  const trackAsyncOperation = useCallback(async <T>(
    operationName: string,
    operation: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> => {
    const startTime = Date.now();
    
    try {
      const result = await operation();
      
      await trackResponseTime(operationName, startTime, {
        success: true,
        ...metadata
      });
      
      return result;
    } catch (error) {
      await logError('async_operation_failed', error instanceof Error ? error.message : 'Unknown error', {
        operation_name: operationName,
        duration: Date.now() - startTime,
        ...metadata
      });
      
      throw error;
    }
  }, [trackResponseTime, logError]);

  return {
    // Core logging functions
    logAction,
    logClick,
    logFormSubmit,
    logSearch,
    logError,
    logDownload,
    
    // Performance tracking
    trackResponseTime,
    trackAsyncOperation,
    
    // Direct access to logger instance
    logger: userActionLogger,
    
    // Session info
    sessionId: userActionLogger.getSessionId(),
    queueLength: userActionLogger.getQueueLength()
  };
}